import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import PropTypes from "prop-types";
import moment from "moment";

const WEEKDAYS = [
  { key: "sun", label: "S" },
  { key: "mon", label: "M" },
  { key: "tue", label: "T" },
  { key: "wed", label: "W" },
  { key: "thu", label: "T" },
  { key: "fri", label: "F" },
  { key: "sat", label: "S" },
];

const ORDINALS = ["first", "second", "third", "fourth", "fifth"];

const getMonthlyLabels = (date) => {
  if (!date || !moment.isMoment(date)) {
    const now = moment();
    return {
      dayOfMonth: now.date(),
      weekdayLabel: `${ORDINALS[Math.floor((now.date() - 1) / 7)]} ${now.format("dddd")}`,
    };
  }
  const dayOfMonth = date.date();
  const weekOfMonth = Math.floor((dayOfMonth - 1) / 7);
  const weekdayName = date.format("dddd");
  return {
    dayOfMonth,
    weekdayLabel: `${ORDINALS[weekOfMonth]} ${weekdayName}`,
  };
};

const CustomRecurrenceDialog = ({
  open,
  onClose,
  selectedDate,
  repeatEvery,
  repeatUnit,
  monthlyPattern,
  weeklyDays,
  endMode,
  endOnDate,
  endAfterCount,
  onRepeatEveryChange,
  onRepeatUnitChange,
  onMonthlyPatternChange,
  onWeeklyDaysChange,
  onEndModeChange,
  onEndOnDateChange,
  onEndAfterCountChange,
}) => {
  const { dayOfMonth, weekdayLabel } = getMonthlyLabels(selectedDate);
  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="h5" color="textPrimary" gutterBottom>
          Custom recurrence
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={8}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography sx={{ minWidth: 96 }}>Repeat every</Typography>
              <TextField
                type="number"
                size="small"
                value={repeatEvery}
                onChange={(e) =>
                  onRepeatEveryChange(Math.max(1, Number(e.target.value) || 1))
                }
                sx={{ width: 72 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={repeatUnit}
                  onChange={(e) => onRepeatUnitChange(e.target.value)}
                >
                  <MenuItem value="day">day</MenuItem>
                  <MenuItem value="week">week</MenuItem>
                  <MenuItem value="month">month</MenuItem>
                  <MenuItem value="year">year</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {repeatUnit === "month" && (
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <Select
                  value={monthlyPattern}
                  onChange={(e) => onMonthlyPatternChange(e.target.value)}
                >
                  <MenuItem value="day-of-month">
                    Monthly on day {dayOfMonth}
                  </MenuItem>
                  <MenuItem value="weekday-position">
                    Monthly on the {weekdayLabel}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}

          {repeatUnit === "week" && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Repeat on
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {WEEKDAYS.map((day) => {
                  const isSelected = weeklyDays?.includes(day.key);
                  return (
                    <Box
                      key={day.key}
                      onClick={() => {
                        const newDays = isSelected
                          ? weeklyDays.filter((d) => d !== day.key)
                          : [...(weeklyDays || []), day.key];
                        onWeeklyDaysChange(newDays);
                      }}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        bgcolor: isSelected ? "primary.main" : "grey.200",
                        color: isSelected ? "white" : "text.primary",
                        fontWeight: 500,
                        transition: "all 0.2s",
                        "&:hover": {
                          bgcolor: isSelected ? "primary.dark" : "grey.300",
                        },
                      }}
                    >
                      {day.label}
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Ends
            </Typography>
            <RadioGroup
              value={endMode}
              onChange={(e) => onEndModeChange(e.target.value)}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: endMode === "on" ? "action.selected" : "transparent",
                  opacity: endMode === "on" ? 1 : 0.5,
                  transition: "all 0.2s ease",
                }}
              >
                <FormControlLabel value="on" control={<Radio />} label="On" />
                <Box sx={{ ml: 2, flex: 1 }}>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                      value={endOnDate}
                      onChange={(value) =>
                        onEndOnDateChange(value || endOnDate)
                      }
                      disabled={endMode !== "on"}
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: { width: "100%" },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: endMode === "after" ? "action.selected" : "transparent",
                  opacity: endMode === "after" ? 1 : 0.5,
                  transition: "all 0.2s ease",
                }}
              >
                <FormControlLabel
                  value="after"
                  control={<Radio />}
                  label="After"
                />
                <TextField
                  type="number"
                  size="small"
                  value={endAfterCount}
                  onChange={(e) =>
                    onEndAfterCountChange(
                      Math.max(1, Number(e.target.value) || 1)
                    )
                  }
                  disabled={endMode !== "after"}
                  sx={{ width: 80, ml: 2, mr: 1 }}
                />
                <Typography color="textSecondary">occurrences</Typography>
              </Box>
            </RadioGroup>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <Button variant="text" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="contained" onClick={onClose}>
                Done
              </Button>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

CustomRecurrenceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedDate: PropTypes.object,
  repeatEvery: PropTypes.number.isRequired,
  repeatUnit: PropTypes.string.isRequired,
  monthlyPattern: PropTypes.string.isRequired,
  weeklyDays: PropTypes.arrayOf(PropTypes.string),
  endMode: PropTypes.string.isRequired,
  endOnDate: PropTypes.object.isRequired,
  endAfterCount: PropTypes.number.isRequired,
  onRepeatEveryChange: PropTypes.func.isRequired,
  onRepeatUnitChange: PropTypes.func.isRequired,
  onMonthlyPatternChange: PropTypes.func.isRequired,
  onWeeklyDaysChange: PropTypes.func,
  onEndModeChange: PropTypes.func.isRequired,
  onEndOnDateChange: PropTypes.func.isRequired,
  onEndAfterCountChange: PropTypes.func.isRequired,
};

export default CustomRecurrenceDialog;
